/**
 * パスキーのみでのログイン（ID・パスワードを入力しない。管理画面・マイページ共通）
 *
 * - Conditional UI: 画面を開いたら {prefix}/login/passkey/options でチャレンジを受け取り、
 *   ID 欄（autocomplete="username webauthn"）の自動入力候補にパスキーを出して待ち受ける
 * - 「パスキーでログイン」ボタン: 自動入力に対応しない環境向けに、パスキーのダイアログを開く
 * どちらも選ばれたパスキーのアサーションを {prefix}/login/passkey に送り、ログイン後のURLへ遷移する。
 *
 * ボタンの欄（[data-passkey-login]）は、ログイン画面の「他の方法でログイン」（[data-login-other]）の中に置き、panel で渡す。
 * 自動入力の待ち受けは、ほかのパスキー操作（ボタン・パスワードログインの2段階目）を始めると中断されるため、
 * それらが終わったら armConditional() で再開する。
 */
import {
    browserSupportsWebAuthn,
    browserSupportsWebAuthnAutofill,
    startAuthentication,
} from '@simplewebauthn/browser';
import { COMMON_ERROR_MESSAGES, DEFAULT_ERROR_MESSAGE, postJson, readError } from '@/js/_components/passkey/login';

const MESSAGES = {
    ...COMMON_ERROR_MESSAGES,
    PASSKEY_CANCELLED: 'パスキーでのログインがキャンセルされました',
};

/** 別のパスキー操作で待ち受けが中断されたときのエラーか */
const isAborted = (e) => !!e && (e.name === 'AbortError' || e.code === 'ERROR_CEREMONY_ABORTED');

/**
 * @param {Object} params
 * @param {HTMLFormElement} params.form ログインフォーム（remember の値を使う）
 * @param {string} params.optionsUrl {prefix}/login/passkey/options
 * @param {string} params.loginUrl {prefix}/login/passkey
 * @param {HTMLElement|null} [params.panel] ボタンの欄（[data-passkey-login]）
 * @returns {{ armConditional: () => Promise<void> }}
 */
export const setupPasskeyLogin = ({ form, optionsUrl, loginUrl, panel }) => {
    const button = panel ? panel.querySelector('[data-passkey-login-button]') : null;
    const errorArea = panel ? panel.querySelector('[data-passkey-login-error]') : null;
    let busy = false;

    const showError = (code) => {
        if (!errorArea) return;
        errorArea.textContent = MESSAGES[code] ?? DEFAULT_ERROR_MESSAGE;
        errorArea.hidden = false;
    };
    const clearError = () => {
        if (!errorArea) return;
        errorArea.textContent = '';
        errorArea.hidden = true;
    };

    const remember = () => {
        const input = form.querySelector('[name="remember"]');
        return !!input && (input.type === 'checkbox' ? input.checked : !!input.value);
    };

    const fetchOptions = async () => {
        const response = await postJson(optionsUrl, {});
        if (!response.ok) {
            const error = new Error('PASSKEY_OPTIONS_FAILED');
            error.code = await readError(response);
            throw error;
        }
        const { options } = await response.json();
        return options;
    };

    /** アサーションを送ってログインする。ログインできたら true */
    const finish = async (assertion) => {
        const response = await postJson(loginUrl, {
            assertion: JSON.stringify(assertion),
            remember: remember(),
        });
        if (response.ok) {
            const { redirect } = await response.json();
            window.location.href = redirect;
            return true;
        }
        showError(await readError(response));
        return false;
    };

    let autofillSupported = null;

    /** 自動入力の候補にパスキーを出して待ち受ける（失敗したら待ち受けをやり直す） */
    const armConditional = async () => {
        if (autofillSupported === null) {
            autofillSupported = await browserSupportsWebAuthnAutofill().catch(() => false);
        }
        if (!autofillSupported) return;

        let assertion;
        try {
            const options = await fetchOptions();
            assertion = await startAuthentication({ optionsJSON: options, useBrowserAutofill: true });
        } catch (e) {
            // 中断（ほかのパスキー操作の開始）・オプションの取得失敗では待ち受けをやり直さない
            if (!isAborted(e)) console.warn(e);
            return;
        }

        clearError();
        try {
            if (await finish(assertion)) return;
        } catch (e) {
            console.error(e);
            showError(null);
        }
        // チャレンジの期限切れ・未登録のパスキーなど。もう一度選べるよう待ち受けをやり直す
        armConditional();
    };

    if (panel && button && browserSupportsWebAuthn()) {
        panel.hidden = false;
        button.addEventListener('click', async (e) => {
            e.preventDefault();
            if (busy) return;
            busy = true;
            button.disabled = true;
            clearError();
            try {
                const options = await fetchOptions();
                // 自動入力の待ち受けは startAuthentication が中断する
                const assertion = await startAuthentication({ optionsJSON: options });
                if (await finish(assertion)) return;
            } catch (error) {
                if (error && error.name === 'NotAllowedError') {
                    showError('PASSKEY_CANCELLED');
                } else {
                    console.error(error);
                    showError(error && error.code ? error.code : null);
                }
            } finally {
                busy = false;
                button.disabled = false;
            }
            armConditional();
        });
    }

    return { armConditional };
};
